// GoogleCalendarSetupGuide — Feb 20, 2026 (Randy, Plan B rewrite)
//
// Two rendering modes:
//
//   1) SHARED-CLIENT MODE (default, once the admin has configured the
//      platform-wide Google OAuth client via Go-Live Wizard → Admin: Google
//      Cloud Setup). Regular users see a friendly ~15-second explainer with
//      no Google Cloud Console content whatsoever. Everything they need is
//      one Connect button.
//
//   2) LEGACY BYOK FALLBACK. Shown only when NO shared client is configured
//      (early bootstrap, or if the admin cleared it). This is the "advanced"
//      path — the same Cloud Console walkthrough as before, kept as an
//      escape hatch. Never rendered to normal users once the admin
//      completes the shared setup.

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import {
  X, ArrowSquareOut, Copy, FloppyDisk, GoogleLogo, Trash,
  CheckCircle, WarningCircle, Eye, DeviceMobile, ShieldCheck,
} from "@phosphor-icons/react";

const REDIRECT_PATH = "/api/oauth/calendar/callback";

// Shared, ultra-minimal step block for the fallback BYOK path.
const Step = ({ n, title, children }) => (
  <div className="pb-4 border-b hairline last:border-0">
    <div className="text-sm font-medium text-[#1C1B1A] mb-1.5">{n} · {title}</div>
    <div className="text-xs text-[#2C2B2A] leading-relaxed space-y-2">{children}</div>
  </div>
);
const Ext = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer"
    className="inline-flex items-center gap-1.5 text-xs text-[#4A5D4E] hover:text-[#1C1B1A] hover:underline break-all">
    <ArrowSquareOut size={12} weight="duotone" /> {children}
  </a>
);

export default function GoogleCalendarSetupGuide({ open, onClose, onSaved }) {
  const [status, setStatus] = useState(null);
  const [cid, setCid] = useState("");
  const [secret, setSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const redirectUri = `${window.location.origin}${REDIRECT_PATH}`;

  useEffect(() => {
    if (!open) {
      setCid(""); setSecret(""); setSaving(false); setTesting(false);
      setTestResult(null); setAdvancedOpen(false);
      return;
    }
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    api.get("/calendar/google/credentials").then((r) => setStatus(r.data)).catch(() => {});
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const copy = async (text, msg = "Copied.") => {
    try { await navigator.clipboard.writeText(text); toast.success(msg); }
    catch { toast.error("Couldn't copy."); }
  };

  const save = async () => {
    setSaving(true); setTestResult(null);
    try {
      await api.post("/calendar/google/credentials", { client_id: cid.trim(), client_secret: secret.trim() });
      toast.success("Saved. Hit Test to make sure Google recognizes it.");
      const r = await api.get("/calendar/google/credentials");
      setStatus(r.data); setCid(""); setSecret("");
      onSaved?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/calendar/google/credentials/test", { origin: window.location.origin });
      setTestResult(r.data);
      if (r.data.ok) toast.success("Google says you're good to go.");
      else toast.error("Test failed — see details below.", { duration: 6000 });
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || "Test failed — try again." });
    } finally { setTesting(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove your Google connection? This also disconnects any linked Google Calendar until you set it up again.")) return;
    try {
      await api.post("/calendar/google/credentials", { clear: true });
      const r = await api.get("/calendar/google/credentials");
      setStatus(r.data); setTestResult(null);
      onSaved?.(r.data);
      toast.success("Google connection removed.");
    } catch { toast.error("Couldn't remove."); }
  };

  const shared = status?.shared_configured;

  return createPortal(
    <div className="fixed inset-0 z-[210] flex items-center justify-center p-4" data-testid="gcal-setup-modal">
      <div className="absolute inset-0 bg-[#1C1B1A]/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg max-h-[85vh] bg-white border hairline rounded-xl shadow-2xl overflow-hidden flex flex-col">
        <div className="flex items-start justify-between px-6 py-4 border-b hairline bg-[#FDFCF8]">
          <div>
            <div className="font-serif-display text-lg text-[#1C1B1A] flex items-center gap-2">
              <GoogleLogo size={18} weight="bold" /> Connect Google Calendar
            </div>
            <div className="text-xs text-[#2C2B2A] mt-1">
              {shared
                ? "Read-only import. Free. Takes ~15 seconds."
                : "Advanced setup (bring your own OAuth client) — the shared platform client hasn't been configured yet."}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md text-[#2C2B2A] hover:bg-white" data-testid="gcal-setup-close"><X size={16} /></button>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-5">

          {/* ============== SHARED-CLIENT MODE (99% of users see this) ============== */}
          {shared && (
            <>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-[#EAF0EB] border border-[#4A5D4E]/30" data-testid="gcal-shared-active">
                <ShieldCheck size={22} weight="fill" className="text-[#2F6B3A] shrink-0 mt-0.5" />
                <div className="text-sm text-[#1C1B1A] leading-relaxed">
                  <strong className="block">Ready to connect.</strong>
                  You'll approve My Silo on Google's screen, come back here, and every event on your (or your family's shared) Google Calendar will flow into your silo's AI Calendar. <strong>Read-only</strong> — My Silo never writes or deletes anything on Google.
                </div>
              </div>

              <div className="space-y-3 text-sm text-[#1C1B1A]">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#F0F1EE] text-[#2C2B2A] flex items-center justify-center text-xs font-medium shrink-0 mt-0.5">1</div>
                  <div className="flex-1">Open your silo → <strong>Staging</strong> → <strong>AI Calendar</strong> → tap <strong>Connect Google Calendar</strong>. A new tab opens on Google.</div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#F0F1EE] text-[#2C2B2A] flex items-center justify-center text-xs font-medium shrink-0 mt-0.5">2</div>
                  <div className="flex-1">
                    Pick your Google account → <strong>Continue</strong>. If Google warns "Google hasn't verified this app," tap <strong>Advanced → Continue anyway</strong>. That's normal for a family app in launch phase — safe.
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#F0F1EE] text-[#2C2B2A] flex items-center justify-center text-xs font-medium shrink-0 mt-0.5">3</div>
                  <div className="flex-1">Approve the read-only access → the tab closes (or you close it) → back on My Silo, hit <strong>Sync now</strong>.</div>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-[#F0F1EE] border-l-2 border-[#4A5D4E]">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#4A5D4E] mb-1">One-account-per-family tip</div>
                <div className="text-xs text-[#1C1B1A] leading-relaxed">
                  Connect from the Google account that already receives shared family calendars (spouse's calendar, kids' calendars). One approval pulls the whole family. Only add more accounts if a family member is running My Silo separately with their OWN silo.
                </div>
              </div>

              <button onClick={() => setAdvancedOpen((v) => !v)} data-testid="gcal-advanced-toggle"
                className="text-[11px] text-[#2C2B2A] hover:text-[#1C1B1A] underline decoration-dotted underline-offset-4">
                {advancedOpen ? "Hide advanced options" : "Advanced: bring my own OAuth client instead"}
              </button>
            </>
          )}

          {/* ============== BYOK FALLBACK (admin hasn't configured shared yet, OR user expanded Advanced) ============== */}
          {(!shared || advancedOpen) && (
            <div className="space-y-5">
              {shared && (
                <div className="text-[11px] p-2 rounded bg-[#FBEBE4] border border-[#E8D5CF] text-[#1C1B1A]">
                  <strong>Advanced only.</strong> You don't need any of this — the platform's shared Google client already works. Only paste your own credentials if you want your family's calendar events to flow through YOUR own Google Cloud project instead of the shared one.
                </div>
              )}

              {!shared && (
                <p className="text-sm text-[#1C1B1A] leading-relaxed">
                  Once connected, your (and your family's shared) Google Calendar events flow straight into your silo's AI Calendar. The connection is <strong>read-only</strong> and free. Below is your own OAuth client walkthrough.
                </p>
              )}

              {/* Paste form */}
              <div className="bg-[#FDFCF8] border-2 border-[#4A5D4E]/20 rounded-lg p-4" data-testid="gcal-setup-form">
                <div className="text-xs uppercase tracking-[0.2em] text-[#4A5D4E] mb-2 flex items-center gap-1.5">
                  <FloppyDisk size={12} weight="duotone" /> Your OAuth credentials
                </div>
                {status?.has_credentials && (
                  <div className="mb-2 flex items-center gap-2 text-[11px] text-[#2F6B3A] bg-[#EAF0EB] rounded px-2.5 py-1.5" data-testid="gcal-creds-status">
                    <span className="flex-1 font-mono truncate">Saved · {status.client_id_masked}{status.connected ? ` · connected as ${status.email || "Google"}` : " · not connected yet"}</span>
                    <button onClick={remove} data-testid="gcal-creds-remove" className="text-[#C16E5A] hover:underline flex items-center gap-1"><Trash size={11} /> Remove</button>
                  </div>
                )}
                <div className="space-y-2">
                  <input value={cid} onChange={(e) => setCid(e.target.value)} data-testid="gcal-client-id-input" autoComplete="off"
                    placeholder="Client ID — ends in .apps.googleusercontent.com"
                    className="w-full bg-white border hairline rounded-md px-3 py-2 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
                  <div className="flex gap-2 items-stretch">
                    <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} data-testid="gcal-client-secret-input" autoComplete="off"
                      placeholder="Client Secret — starts with GOCSPX-"
                      className="flex-1 bg-white border hairline rounded-md px-3 py-2 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
                    <button onClick={save} disabled={!cid.trim() || !secret.trim() || saving} data-testid="gcal-creds-save"
                      className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-60 flex items-center gap-1">
                      <FloppyDisk size={12} weight="duotone" /> {saving ? "Saving…" : "Save"}
                    </button>
                  </div>
                  {status?.has_credentials && (
                    <button onClick={test} disabled={testing} data-testid="gcal-creds-test"
                      className="w-full text-xs px-4 py-2 rounded-md border-2 border-[#4A5D4E] text-[#4A5D4E] hover:bg-[#4A5D4E] hover:text-white disabled:opacity-60 flex items-center justify-center gap-1.5 font-medium">
                      <CheckCircle size={13} weight="duotone" /> {testing ? "Asking Google…" : "Test my Google connection"}
                    </button>
                  )}
                  {testResult && testResult.ok && (
                    <div className="flex items-start gap-2 text-[11px] bg-[#EAF0EB] border border-[#4A5D4E]/30 rounded-md p-3" data-testid="gcal-test-ok">
                      <CheckCircle size={16} weight="fill" className="text-[#2F6B3A] shrink-0 mt-0.5" />
                      <div className="text-[#1C1B1A]"><strong className="block mb-0.5">Google says you're all set.</strong>{testResult.message}</div>
                    </div>
                  )}
                  {testResult && !testResult.ok && (
                    <div className="flex items-start gap-2 text-[11px] bg-[#FBEBE4] border border-[#E8D5CF] rounded-md p-3" data-testid="gcal-test-fail">
                      <WarningCircle size={16} weight="fill" className="text-[#C16E5A] shrink-0 mt-0.5" />
                      <div className="text-[#1C1B1A] whitespace-pre-wrap leading-relaxed">{testResult.error}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Redirect URI (needed to paste in Google Cloud Console) */}
              <div className="bg-[#F0F1EE] rounded-md p-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#2C2B2A] mb-1.5">Paste this into "Authorized redirect URIs" on Google's side</div>
                <div className="flex items-center gap-2">
                  <code className="text-[11px] font-mono text-[#1C1B1A] break-all flex-1 select-all" data-testid="gcal-redirect-uri">{redirectUri}</code>
                  <button onClick={() => copy(redirectUri, "Copied.")} data-testid="gcal-redirect-copy"
                    className="p-1.5 rounded text-[#2C2B2A] hover:text-[#1C1B1A] hover:bg-white" title="Copy"><Copy size={12} /></button>
                </div>
              </div>

              <div className="space-y-4">
                <div className="text-xs uppercase tracking-[0.2em] text-[#2C2B2A]">6 steps · ~5 min</div>
                <Step n="1" title="Google Cloud project">
                  <p>Sign in to <Ext href="https://console.cloud.google.com/projectcreate">console.cloud.google.com/projectcreate</Ext>. Create a project (or reuse your existing one).</p>
                </Step>
                <Step n="2" title={"Enable \"Google Calendar API\""}>
                  <p><Ext href="https://console.cloud.google.com/apis/library/calendar-json.googleapis.com">Direct link to enable</Ext> — pick your project in the top selector, tap Enable.</p>
                </Step>
                <Step n="3" title="Audience: add yourself as a test user">
                  <p><Ext href="https://console.cloud.google.com/auth/audience">console.cloud.google.com/auth/audience</Ext></p>
                  <div className="flex items-start gap-2 bg-[#FBEBE4] border-l-2 border-[#C16E5A] rounded-r-md p-2.5">
                    <DeviceMobile size={13} weight="duotone" className="text-[#C16E5A] shrink-0 mt-0.5" />
                    <span className="text-[11px]">On mobile the sidebar hides behind the hamburger. The direct link above skips that.</span>
                  </div>
                  <p><strong>User type: External</strong> · <strong>Publishing status: Testing</strong> · scroll to <strong>Test users → + Add users</strong> → add YOUR email (+ any family Gmails that'll connect their own calendars).</p>
                </Step>
                <Step n="4" title="Create OAuth Client ID (Web application)">
                  <p><Ext href="https://console.cloud.google.com/apis/credentials">Credentials page</Ext> → + Create Credentials → OAuth client ID → Web application. Name it "My Silo Calendar".</p>
                  <div className="flex items-start gap-2 bg-[#F0F1EE] border-l-2 border-[#4A5D4E] rounded-r-md p-2.5">
                    <span className="text-[#4A5D4E] text-[10px] uppercase tracking-[0.2em] font-medium shrink-0 mt-0.5">Skip</span>
                    <span className="text-[11px]"><strong>Authorized JavaScript origins</strong> — leave EMPTY. Only the redirect URIs section matters.</span>
                  </div>
                  <p>Scroll to <strong>Authorized redirect URIs → + Add URI</strong> → paste the URI from the box above → Save. Wait ~5 min for Google to propagate.</p>
                </Step>
                <Step n="5" title="Grab Client ID + Secret">
                  <p>Client ID ends in <code className="font-mono">.apps.googleusercontent.com</code>; Secret starts with <code className="font-mono">GOCSPX-</code>.</p>
                  <div className="flex items-start gap-2 bg-[#FBEBE4] border-l-2 border-[#C16E5A] rounded-r-md p-2.5">
                    <DeviceMobile size={13} weight="duotone" className="text-[#C16E5A] shrink-0 mt-0.5" />
                    <span className="text-[11px]">iPhone gotcha: the secret sits off-screen right. Tap the <Eye size={11} weight="duotone" className="inline mx-0.5" /> eye icon at the top-right of the credential row to reveal it, then long-press to copy.</span>
                  </div>
                  <p><strong>Missed the secret at creation?</strong> Client secrets → + Add secret → copy IMMEDIATELY → delete old one. Client ID stays.</p>
                </Step>
                <Step n="6" title="Paste + Save + Test — done">
                  <p>Paste values into the form up top → Save → Test my Google connection. Green check = ready. Then open your silo → Staging → AI Calendar → Connect Google Calendar.</p>
                </Step>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
